import { FC, useCallback, useMemo, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import { Box, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Boy, MeetingRoom, Person } from '@mui/icons-material';

import { 
  HotelAmentityDTO,
  HotelAvailabilityEmit,
  HotelBookingApiResponseDTO,
  HotelQuoteApiResponseDTO,
} from '@bandido/shared/dtos';
import { HotelFareMetadataResponseDto } from '@bandido/shared/dtos/clients/get-saleorder.dto';
import { CartWebSocketsEvents } from '@bandido/shared/types';

import { calculateAdultsAndMinors } from '@frontend/features/cart/components/CartItem/helper';
import { DetailCard, useHotelMetadata } from '@frontend/features/hotel-content';
import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';
import { QueryKeys } from '@frontend/features/common/types';
import { BookStatusType } from '@frontend/features/sale-orders/types';
import { useGetSaleOrderPaxes } from '@frontend/features/sale-orders/queries';
import { useWS } from '@frontend/features/common/hooks/useWS';
import { CONFIG } from '@frontend/config';

import { HotelHolderData } from '../holder-data';
import { Form } from '../holder-data/form';
import { SaleOrderHotelDetail } from './detail';
import { SaleOrderHotelRooms } from './rooms';
import { cancelHotelItemBook, emitHotelItemBook } from './services';
import { 
  CardInfoWrapper,
  DetailCardContainer,
  BookingDetailsCard,
  BookingTitle,
  InfoContainer,
  MonospaceText,
  BookingFooter,
} from './styles';
import { HotelBookingWizard } from '../hotel-booking-wizard';

interface Props {
  hotel: {
    nimbuxHotelId: number;
    categoryId: number;
    address: string;
    amenities: HotelAmentityDTO[];
    chainCode: string;
    city: string;
    country: string;
    hotelCode: string;
    name: string;
    picture: string;
    zipCode: string;
    providerHotelName: string;
  };
  id: string;
  metadata: HotelFareMetadataResponseDto,
  booking?: HotelBookingApiResponseDTO;
  quotation?: HotelQuoteApiResponseDTO;
}

export const SaleOrderHotel:FC<Props> = ({ id, hotel, metadata, booking, quotation }) => {
  const {
    occupancies = [],
    rooms = [],
    accessCode,
    context,
    checkinDate,
    checkoutDate,
    cancelPolicy,
    boardDescription,
  } = metadata;
  const { findSupplierByAccessCode } = useHotelMetadata();
  const { addToast } = useSnackBar();
  const { t } = useTranslation();
  const { id: saleOrderId } = useParams();
  const [ isEmitting, setIsEmitting ] = useState(false);
  const [ isCancelling, setIsCancelling ] = useState(false);
  const [ isOpenWizard, setIsOpenWizard ] = useState(false);
  const { data: paxes } = useGetSaleOrderPaxes({ id: saleOrderId });
  const { socketRef, joinRoom, leaveRoom } = useWS(`${CONFIG.WS_URL}/cart`);

  const queryClient = useQueryClient();

  const { adults, minors } = useMemo(() => calculateAdultsAndMinors(occupancies), [hotel]);

  const handleCancelHotelBook = useCallback(async () => {
    if (booking?.reference.bookingID && saleOrderId) {
      setIsCancelling(true);
      const status = await cancelHotelItemBook({
        saleOrderId,
        bookingId: booking.id,
        bookingIdTGX: booking.reference.bookingID,
        context: context
      });

      if (status) {
        addToast({
          color: 'success',
          message: t('saleOrder.view.hotels.cancelSuccess'),
        });
        await queryClient.invalidateQueries({ queryKey: [QueryKeys.getSaleOrdersDetails] });
        await queryClient.invalidateQueries({ queryKey: [QueryKeys.getSaleOrdersItems] });
        await queryClient.invalidateQueries({ queryKey: [QueryKeys.getSaleOrdersForDashboard] });
      } else {
        addToast({
          color: 'error',
          message: t('common.error.server'),
        });
      }
    } else {
      addToast({
        color: 'error',
        message: t('common.error.client'),
      });
    }
    setIsCancelling(false);
  }, [booking, context, setIsCancelling]);

  const handleEmitHotelBook = useCallback(async (data: Form) => {
    if (quotation?.optionRefId && saleOrderId && socketRef.current?.id) {
      setIsEmitting(true);
      setIsOpenWizard(false);
      const status = await emitHotelItemBook({
        saleOrderId,
        saleOrderItemId: id,
        clientReference: `ORDER_${saleOrderId}`,
        holder: {
          name: data.name,
          surname: data.surname,
          email: data.email || undefined,
        },
        socketId: socketRef.current.id,
      });

      if (!status) {
        addToast({
          color: 'error',
          message: t('common.error.server'),
        });
      }
    } else {
      addToast({
        color: 'error',
        message: t('common.error.client'),
      });
    }
  }, [quotation, saleOrderId, socketRef, addToast, t]);

  const hotelOrderSummary = useMemo(() => {
    if (booking) {
      return {
        status: booking.status,
        net: Number(booking.price.net),
        gross: Number(booking.price.gross),
      }
    } else if (quotation) {
      return {
        status: quotation.status,
        net: Number(quotation.price.net),
        gross: Number(quotation.price.gross),
      }
    }
    return {
      status: BookStatusType.PENDING,
      net: 0,
      gross: 0,
    }
  }, [booking, quotation]);

  useEffect(() => {
    if (socketRef.current?.id && saleOrderId) {
      joinRoom( JSON.stringify({
        saleOrderId,
      }));

      socketRef.current.on(CartWebSocketsEvents.BOOKING_SUCESS, async (data: string) => {
        try {
          const { saleOrderId: responseSaleOrderId, saleOrderItemId: responseSaleOrderItemId } = JSON.parse(data);
          if (responseSaleOrderId === saleOrderId && responseSaleOrderItemId === id) {
            addToast({
              color: 'success',
              message: t('saleOrder.view.hotels.emitSuccess'),
            });
            await queryClient.invalidateQueries({ queryKey: [QueryKeys.getSaleOrdersDetails] });
            await queryClient.invalidateQueries({ queryKey: [QueryKeys.getSaleOrdersItems] });
            await queryClient.invalidateQueries({ queryKey: [QueryKeys.getSaleOrdersForDashboard] });
          }
        } catch (error) {
          addToast({
            color: 'error',
            message: t('saleOrder.view.hotels.emitError'),
          });
          console.error('Error parsing booking response:', error);
        } finally {
          setIsEmitting(false);
        }
      });

      socketRef.current.on(CartWebSocketsEvents.BOOKING_ERROR, (error: string) => {
        try {
          const { saleOrderId: responseSaleOrderId, saleOrderItemId: responseSaleOrderItemId, error: parsedError } = JSON.parse(error);
          if (responseSaleOrderId === saleOrderId && responseSaleOrderItemId === id) {
            addToast({
              color: 'error',
              message: parsedError.message || t('common.error.server'),
            });
          }
        } catch (error) {
          console.error('Error parsing booking error:', error);
        } finally {
          setIsEmitting(false);
        }
      });

      return () => {
        socketRef.current?.off(CartWebSocketsEvents.BOOKING_SUCESS);
        socketRef.current?.off(CartWebSocketsEvents.BOOKING_ERROR);
        leaveRoom(JSON.stringify({
          saleOrderId,
        }));
      };
    }
  }, [socketRef.current?.id, saleOrderId]);

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <CardInfoWrapper>
        <DetailCardContainer>
          <DetailCard
            isCompact
            hotel={hotel as unknown as HotelAvailabilityEmit}
            footer={(
              <Box  display="flex" gap={0.5} flexDirection="column">
                <Box display="flex" gap={2} justifyContent="space-between">
                  <Box display="flex" gap={3} alignItems="center">
                    <Box display="flex" gap={1}>
                      <MeetingRoom color="primary" />
                      <Typography variant="subtitle2">{occupancies.length}</Typography>
                    </Box>

                    <Box display="flex" gap={1}>
                      <Person color="primary" />
                      <Typography variant="subtitle2">{adults}</Typography>
                    </Box>
                      
                    {minors > 0 && <Box display="flex" gap={1}>
                      <Boy color="primary" />
                      <Typography variant="subtitle2">{minors}</Typography>
                    </Box>}
                  </Box>
                </Box>
              </Box>
            )}
          />
        </DetailCardContainer>
        
        <BookingDetailsCard>
          <BookingTitle variant="h6">
            --- {t('saleOrder.bookingDetails.title')} ---
          </BookingTitle>
          
          <InfoContainer>
            <MonospaceText variant="body2">
              {t('saleOrder.bookingDetails.hotelName')}:
              <br />
              <b>{hotel.providerHotelName}</b>
            </MonospaceText>
            
            <MonospaceText variant="body2">
              {t('saleOrder.bookingDetails.provider')}:
              <br />
              <b>{findSupplierByAccessCode(accessCode)?.supplierName || t('common.notValueFound')}</b>
            </MonospaceText>
            
            <MonospaceText variant="body2">
              {t('saleOrder.bookingDetails.reference')}:
              <br />
              <b>{booking?.reference.supplier || t('common.tbd')}</b>
            </MonospaceText>
          </InfoContainer>
          
          <BookingFooter variant="caption">
            ----------------------------
          </BookingFooter>
        </BookingDetailsCard>
      </CardInfoWrapper>
      
      { hotelOrderSummary?.status !== BookStatusType.PENDING && (
        <Box display="flex" gap={2} width="200" justifyContent="flex-end">
          <LoadingButton
            variant='contained'
            loading={isCancelling}
            disabled={!booking || hotelOrderSummary?.status === BookStatusType.CANCELLED}
            onClick={handleCancelHotelBook}
          >
            { booking && hotelOrderSummary?.status === BookStatusType.CANCELLED ? t('saleOrder.view.hotels.cancelled') : t('saleOrder.view.hotels.cancel')}
          </LoadingButton>

          <LoadingButton
            loading={isEmitting}
            disabled={Boolean(booking)}
            variant='contained'
            color='secondary'
            onClick={() => setIsOpenWizard(true)}
          >
            {t('saleOrder.view.hotels.emit')}
          </LoadingButton>
        </Box>
      )}

      <SaleOrderHotelDetail
        checkIn={checkinDate || t('common.notValueFound')}
        checkOut={checkoutDate || t('common.notValueFound')}
        internalId={booking?.id ? String(booking?.id) : t('common.notValueFound')}
        markup={t('common.notValueFound')}
        net={Number(hotelOrderSummary.gross)}
        total={Number(hotelOrderSummary.gross)}
        cancelPolicy={quotation ? quotation.cancelPolicy : cancelPolicy}
        remarks={quotation?.remarks || ''}
        boardDescription={boardDescription}
        rateRules={metadata.rateRules || []}
      />

      {
        booking && <SaleOrderHotelRooms
          rooms={rooms}
          occupancies={occupancies}
          checkoutDate={checkoutDate}
        />
      }

      {saleOrderId && (
        <HotelBookingWizard
          saleOrderItemId={id}
          saleOrderId={saleOrderId}
          open={isOpenWizard}
          onClose={() => setIsOpenWizard(false)}
          onComplete={handleEmitHotelBook}
          occupancies={occupancies}
          cartPaxes={paxes || []}
          checkinDate={checkinDate}
          isLoading={isEmitting}
        />
      )}
    </Box>
  );
};
